// War network (docs/ART-DIRECTION.md §1, §2): instanced city diamonds, capital octahedrons with halo
// rings and a faction sigil (§2.3), owner-shaped rim rings and solid neon road arcs, all on the emissive layer.
// Cities connect only over land (taxgw-lcaj): every setup edge is a road; water has no connections, and a transport
// craft's trip is drawn by the route layer (render/routes). City art is authored in CSS px and drawn at the frame's
// zoom, capped per city by local spacing (cityArt.ts): small, screen-sized markers with hairline rims that never swell
// over the war when the camera closes in. Every size and brightness comes from the tuning (cityArtOf); setCityArt()
// retunes a built network in place.
//
// Draw calls with a map set: diamonds, capitals, sigils, rims, edges (5).

import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  Color,
  DoubleSide,
  Group,
  InstancedBufferAttribute,
  InstancedMesh,
  LineSegments,
  Matrix4,
  OctahedronGeometry,
  PlaneGeometry,
  Quaternion,
  ShaderMaterial,
  Shape,
  ShapeGeometry,
  Vector3,
} from 'three'
import { InvariantViolation, type CityIndex, type MapCityView, type MapEdgeView, type MatchSetupView, type OwnerValue, type PlayerId } from '@taxwar/net'
import { forEachPathSample } from '@taxwar/content/geometry/routes'
import { MAX_ROUTE_CENTRE_POINTS, ROUTE_STEP_RAD, countRouteSamples } from '../geo/routeTessellation'
import { EMISSIVE_LAYER, assignLayer } from '../layers'
import { factionRimDashes, factionRole, type Palette } from '../palette'
import { GLOBE_ALTITUDE, latLonToVector3 } from '../geo/globeSpace'
import { CITY_ART_AA_MARGIN_PX, cityArtKind, type CityArt, type CityArtKind, type CityArtPx } from './cityArt'
import { EDGE_FRAGMENT, EDGE_VERTEX, NODE_FRAGMENT, NODE_VERTEX, RIM_FRAGMENT, RIM_VERTEX, SIGIL_FRAGMENT, SIGIL_VERTEX } from './networkShaders'

/** How sharply a node's edge glow falls off toward its centre (the brightness itself is tuned). */
const NODE_RIM_POWER = 2
const CAPITAL_SPIN_RAD_PER_SEC = 0.6

/** Capital sigil (ART-DIRECTION §2.3): a flat faction-coloured star floating above the octahedron (tuned size). */
const SIGIL_INNER_FRACTION = 0.45
const SIGIL_POINTS = 5

const RIM_DASH_DUTY = 0.5

/** Arc apex height per radian of path length. */
const EDGE_LIFT_PER_RAD = 0.08

/** The rim quad spans ±1 in its plane; the shader scales it to the rim radius plus the antialiasing margin. */
const QUAD_SIZE = 2
const FULL_TURN = Math.PI * 2
const POSITION_COMPONENTS = 3
const COLOUR_COMPONENTS = 3
const SEGMENT_VERTICES = 2
const Y_AXIS = new Vector3(0, 1, 0)
const Z_AXIS = new Vector3(0, 0, 1)

/** One instanced part of the city art: its mesh (owning its geometry), per-instance colours and spacing caps. */
interface ColourBatch {
  readonly mesh: InstancedMesh
  readonly colours: InstancedBufferAttribute
  /** Per instance: the city art's cap in render units per CSS px (cityArt.ts). */
  readonly caps: InstancedBufferAttribute
}

interface CityNode {
  readonly batch: ColourBatch
  /** Instance index in `batch`. */
  readonly slot: number
  /** Instance index in the sigil batch (capitals only). */
  readonly sigilSlot: number | null
}

interface EdgeSpan {
  readonly a: CityIndex
  readonly b: CityIndex
  readonly firstVertex: number
  readonly vertexCount: number
}

interface BuiltNetwork {
  readonly viewer: PlayerId
  /** In CityIndex order. */
  readonly cities: readonly MapCityView[]
  readonly owners: OwnerValue[]
  readonly nodes: readonly CityNode[]
  readonly diamonds: ColourBatch
  readonly capitals: ColourBatch
  readonly sigils: ColourBatch
  readonly rims: ColourBatch
  readonly rimDashes: InstancedBufferAttribute
  readonly rimRadii: InstancedBufferAttribute
  readonly rimStrokes: InstancedBufferAttribute
  readonly edges: LineSegments
  readonly edgeColours: BufferAttribute
  readonly edgeSpans: readonly EdgeSpan[]
  /** Normalized route progress (0 at a, 1 at b) per vertex, for the owner-colour blend. */
  readonly edgeProgress: Float32Array
  /** Edge indices incident to each city. */
  readonly incidentEdges: readonly (readonly number[])[]
}

export class WarNetworkLayer {
  readonly root = new Group()

  private readonly capitalSpin = { value: 0 }
  /** Render units per CSS px at the surface under the camera; shared by every city-art material, set every frame. */
  private readonly worldPerPx = { value: 0 }
  /** Uniforms the city art sets (applyArtUniforms), shared by the materials that read them. */
  private readonly nodeFill = { value: 0 }
  private readonly nodeEdge = { value: 0 }
  private readonly cityLiftPx = { value: 0 }
  private readonly capitalLiftPx = { value: 0 }
  private readonly rimIntensity = { value: 0 }
  private readonly sigilIntensity = { value: 0 }
  private readonly sigilLiftPx = { value: 0 }
  private readonly edgeIntensity = { value: 0 }
  private readonly diamondMaterial: ShaderMaterial
  private readonly capitalMaterial: ShaderMaterial
  private readonly rimMaterial: ShaderMaterial
  private readonly sigilMaterial: ShaderMaterial
  private readonly edgeMaterial: ShaderMaterial

  private network: BuiltNetwork | null = null
  private palette: Palette
  private art: CityArt

  private readonly scratchColour = new Color()
  private readonly scratchNormal = new Vector3()
  private readonly scratchPosition = new Vector3()
  private readonly scratchScale = new Vector3()
  private readonly scratchRotation = new Quaternion()
  private readonly scratchMatrix = new Matrix4()

  constructor(palette: Palette, art: CityArt) {
    this.palette = palette
    this.art = art
    this.applyArtUniforms(art)
    this.diamondMaterial = nodeMaterial({ value: 0 }, this.cityLiftPx, this.worldPerPx, this.nodeFill, this.nodeEdge)
    this.capitalMaterial = nodeMaterial(this.capitalSpin, this.capitalLiftPx, this.worldPerPx, this.nodeFill, this.nodeEdge)
    this.rimMaterial = new ShaderMaterial({
      uniforms: {
        uWorldPerPx: this.worldPerPx,
        uMarginPx: { value: CITY_ART_AA_MARGIN_PX },
        uIntensity: this.rimIntensity,
        uDashDuty: { value: RIM_DASH_DUTY },
      },
      vertexShader: RIM_VERTEX,
      fragmentShader: RIM_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    })
    this.sigilMaterial = new ShaderMaterial({
      uniforms: { uWorldPerPx: this.worldPerPx, uLiftPx: this.sigilLiftPx, uIntensity: this.sigilIntensity },
      vertexShader: SIGIL_VERTEX,
      fragmentShader: SIGIL_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      side: DoubleSide,
    })
    this.edgeMaterial = new ShaderMaterial({
      uniforms: { uIntensity: this.edgeIntensity },
      vertexShader: EDGE_VERTEX,
      fragmentShader: EDGE_FRAGMENT,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })
  }

  /**
   * Builds the network for a war, replacing any previous one. Cities must be listed in CityIndex order.
   * `cityArtCaps` (cityArt.ts) caps each city's art by its local spacing.
   */
  setMap(view: MatchSetupView, cityArtCaps: Float32Array): void {
    this.clear()
    const cityCount = view.cities.length
    view.cities.forEach((city, i) => {
      if (city.index !== i) throw new InvariantViolation(`setup city ${city.name} has index ${city.index}, listed at ${i}`)
    })
    for (const edge of view.edges) {
      if (edge.a === edge.b || edge.a < 0 || edge.b < 0 || edge.a >= cityCount || edge.b >= cityCount) {
        throw new InvariantViolation(`setup edge ${edge.a}-${edge.b} does not join two distinct cities`)
      }
    }

    const capitalCount = view.cities.filter((city) => city.capital).length
    const diamonds = createBatch(new OctahedronGeometry(1, 0), this.diamondMaterial, cityCount - capitalCount)
    const capitals = createBatch(new OctahedronGeometry(1, 0), this.capitalMaterial, capitalCount)
    const batches: Readonly<Record<CityArtKind, ColourBatch>> = { city: diamonds, capital: capitals }
    const nextSlot: Record<CityArtKind, number> = { city: 0, capital: 0 }
    const rimGeometry = new PlaneGeometry(QUAD_SIZE, QUAD_SIZE)
    const rimDashes = new InstancedBufferAttribute(new Float32Array(cityCount), 1)
    const rimRadii = new InstancedBufferAttribute(new Float32Array(cityCount), 1)
    const rimStrokes = new InstancedBufferAttribute(new Float32Array(cityCount), 1)
    rimGeometry.setAttribute('aRimDashes', rimDashes)
    rimGeometry.setAttribute('aRadiusPx', rimRadii)
    rimGeometry.setAttribute('aStrokePx', rimStrokes)
    const rims = createBatch(rimGeometry, this.rimMaterial, cityCount)
    const sigils = createBatch(new ShapeGeometry(starShape()), this.sigilMaterial, capitalCount)
    const nodes: CityNode[] = view.cities.map((city) => {
      const kind = cityArtKind(city)
      const slot = nextSlot[kind]++
      return { batch: batches[kind], slot, sigilSlot: kind === 'capital' ? slot : null }
    })

    const edgeBuild = buildEdges(view)
    const edges = new LineSegments(edgeBuild.geometry, this.edgeMaterial)
    // O(cities + edges): a World War has 1,271 cities and thousands of edges.
    const incidentEdges: number[][] = view.cities.map(() => [])
    view.edges.forEach((edge, e) => {
      ;(incidentEdges[edge.a] as number[]).push(e)
      ;(incidentEdges[edge.b] as number[]).push(e)
    })

    const network: BuiltNetwork = {
      viewer: view.you,
      cities: view.cities,
      owners: view.cities.map((city) => city.initialOwner),
      nodes,
      diamonds,
      capitals,
      sigils,
      rims,
      rimDashes,
      rimRadii,
      rimStrokes,
      edges,
      edgeColours: edgeBuild.colours,
      edgeSpans: edgeBuild.spans,
      edgeProgress: edgeBuild.progress,
      incidentEdges,
    }
    for (const object of [diamonds.mesh, capitals.mesh, sigils.mesh, rims.mesh, edges]) assignLayer(object, EMISSIVE_LAYER)
    // All parts are additive and depth-read-only, so their draw order does not change the composite.
    this.root.add(edges, rims.mesh, diamonds.mesh, capitals.mesh, sigils.mesh)
    this.network = network
    this.writeCityArt(network, cityArtCaps)
    this.paintAll(network)
  }

  /** Retunes the city art (the tuner, ROADMAP D33): sizes and brightness now, `cityArtCaps` from the new sizing rule. */
  setCityArt(art: CityArt, cityArtCaps: Float32Array): void {
    this.art = art
    this.applyArtUniforms(art)
    if (this.network) this.writeCityArt(this.network, cityArtCaps)
  }

  setCityOwner(index: CityIndex, owner: OwnerValue): void {
    const network = this.requireNetwork()
    if (index < 0 || index >= network.owners.length) throw new InvariantViolation(`city index ${index} is not on the map`)
    if (network.owners[index] === owner) return
    network.owners[index] = owner
    this.paintCity(network, index)
    const incident = network.incidentEdges[index] as readonly number[]
    for (let i = 0; i < incident.length; i++) this.paintEdge(network, network.edgeSpans[incident[i] as number] as EdgeSpan)
    this.flagColourUploads(network)
  }

  /** Repaints every city and edge from a new palette (a retune or a palette change). */
  setPalette(palette: Palette): void {
    this.palette = palette
    if (this.network) this.paintAll(this.network)
  }

  /** Once per frame, before rendering: animation, and `worldPerCssPixel` (the zoom) sizing the city art. */
  update(dtSeconds: number, worldPerCssPixel: number): void {
    this.capitalSpin.value = (this.capitalSpin.value + dtSeconds * CAPITAL_SPIN_RAD_PER_SEC) % FULL_TURN
    this.worldPerPx.value = worldPerCssPixel
  }

  clear(): void {
    const network = this.network
    if (!network) return
    this.network = null
    this.root.clear()
    for (const batch of [network.diamonds, network.capitals, network.sigils, network.rims]) {
      batch.mesh.geometry.dispose()
      batch.mesh.dispose()
    }
    network.edges.geometry.dispose()
  }

  dispose(): void {
    this.clear()
    this.root.removeFromParent()
    this.diamondMaterial.dispose()
    this.capitalMaterial.dispose()
    this.rimMaterial.dispose()
    this.sigilMaterial.dispose()
    this.edgeMaterial.dispose()
  }

  private requireNetwork(): BuiltNetwork {
    if (!this.network) throw new InvariantViolation('war network has no map')
    return this.network
  }

  private applyArtUniforms(art: CityArt): void {
    this.nodeFill.value = art.brightness.nodeFill
    this.nodeEdge.value = art.brightness.nodeEdge
    this.cityLiftPx.value = nodeLiftPx(art.kinds.city)
    this.capitalLiftPx.value = nodeLiftPx(art.kinds.capital)
    this.rimIntensity.value = art.brightness.rim
    this.sigilIntensity.value = art.brightness.sigil
    const capital = art.kinds.capital
    this.sigilLiftPx.value = capital.nodeHover + 2 * capital.nodeHalfHeight + art.sigil.gap
    this.edgeIntensity.value = art.brightness.edges
  }

  /** Writes every size-dependent instance value: node and sigil extents, rim radii and strokes, and the caps. */
  private writeCityArt(network: BuiltNetwork, cityArtCaps: Float32Array): void {
    if (cityArtCaps.length !== network.cities.length) throw new InvariantViolation(`${cityArtCaps.length} city art caps for ${network.cities.length} cities`)
    const art = this.art
    const normal = this.scratchNormal
    const position = this.scratchPosition
    const scale = this.scratchScale
    const rotation = this.scratchRotation
    const matrix = this.scratchMatrix
    for (const city of network.cities) {
      const px = art.kinds[cityArtKind(city)]
      const cap = cityArtCaps[city.index] as number
      const node = network.nodes[city.index] as CityNode
      latLonToVector3(city.lat, city.lon, 1, normal)
      position.copy(normal).multiplyScalar(GLOBE_ALTITUDE.network)

      // Nodes: the unit octahedron scaled to px half extents, +Y along the normal; the shader lifts it (nodeLiftPx).
      rotation.setFromUnitVectors(Y_AXIS, normal)
      scale.set(px.nodeHalfWidth, px.nodeHalfHeight, px.nodeHalfWidth)
      node.batch.mesh.setMatrixAt(node.slot, matrix.compose(position, rotation, scale))
      node.batch.caps.setX(node.slot, cap)

      // Rims: the surface frame (unit axes, +Z along the normal); the shader sizes the quad from aRadiusPx.
      rotation.setFromUnitVectors(Z_AXIS, normal)
      scale.setScalar(1)
      network.rims.mesh.setMatrixAt(city.index, matrix.compose(position, rotation, scale))
      network.rims.caps.setX(city.index, cap)
      network.rimRadii.setX(city.index, px.rimRadius)
      network.rimStrokes.setX(city.index, px.rimStroke)

      if (node.sigilSlot !== null) {
        scale.setScalar(art.sigil.radius)
        network.sigils.mesh.setMatrixAt(node.sigilSlot, matrix.compose(position, rotation, scale))
        network.sigils.caps.setX(node.sigilSlot, cap)
      }
    }
    for (const batch of [network.diamonds, network.capitals, network.sigils, network.rims]) {
      batch.mesh.instanceMatrix.needsUpdate = true
      batch.caps.needsUpdate = true
    }
    network.rimRadii.needsUpdate = true
    network.rimStrokes.needsUpdate = true
  }

  private paintAll(network: BuiltNetwork): void {
    for (let city = 0; city < network.owners.length; city++) this.paintCity(network, city)
    for (let e = 0; e < network.edgeSpans.length; e++) this.paintEdge(network, network.edgeSpans[e] as EdgeSpan)
    this.flagColourUploads(network)
  }

  private paintCity(network: BuiltNetwork, city: number): void {
    const role = factionRole(network.owners[city] as OwnerValue, network.viewer)
    const colour = this.palette[role]
    const node = network.nodes[city] as CityNode
    node.batch.colours.setXYZ(node.slot, colour.r, colour.g, colour.b)
    if (node.sigilSlot !== null) network.sigils.colours.setXYZ(node.sigilSlot, colour.r, colour.g, colour.b)
    network.rims.colours.setXYZ(city, colour.r, colour.g, colour.b)
    network.rimDashes.setX(city, factionRimDashes(role))
  }

  /** Vertex colours blend from city a's owner colour to city b's along the route progress. */
  private paintEdge(network: BuiltNetwork, span: EdgeSpan): void {
    const colourA = this.palette[factionRole(network.owners[span.a] as OwnerValue, network.viewer)]
    const colourB = this.palette[factionRole(network.owners[span.b] as OwnerValue, network.viewer)]
    const last = span.vertexCount - 1
    for (let i = 0; i <= last; i++) {
      const at = span.firstVertex + i
      this.scratchColour.lerpColors(colourA, colourB, network.edgeProgress[at] as number)
      network.edgeColours.setXYZ(at, this.scratchColour.r, this.scratchColour.g, this.scratchColour.b)
    }
  }

  private flagColourUploads(network: BuiltNetwork): void {
    network.diamonds.colours.needsUpdate = true
    network.capitals.colours.needsUpdate = true
    network.sigils.colours.needsUpdate = true
    network.rims.colours.needsUpdate = true
    network.rimDashes.needsUpdate = true
    network.edgeColours.needsUpdate = true
  }
}

/** An instanced city-art part that owns `geometry`, with per-instance colour and spacing-cap attributes. */
function createBatch(geometry: BufferGeometry, material: ShaderMaterial, count: number): ColourBatch {
  const caps = new InstancedBufferAttribute(new Float32Array(count), 1)
  geometry.setAttribute('aMaxWorldPerPx', caps)
  const mesh = new InstancedMesh(geometry, material, count)
  const colours = new InstancedBufferAttribute(new Float32Array(count * COLOUR_COMPONENTS), COLOUR_COMPONENTS)
  mesh.instanceColor = colours
  return { mesh, colours, caps }
}

/** A unit-radius star in the XY plane. */
function starShape(): Shape {
  const shape = new Shape()
  const vertices = SIGIL_POINTS * 2
  for (let i = 0; i < vertices; i++) {
    const radius = i % 2 === 0 ? 1 : SIGIL_INNER_FRACTION
    const angle = (FULL_TURN * i) / vertices
    const x = radius * Math.sin(angle)
    const y = radius * Math.cos(angle)
    if (i === 0) shape.moveTo(x, y)
    else shape.lineTo(x, y)
  }
  shape.closePath()
  return shape
}

type Uniform = { value: number }

function nodeMaterial(spin: Uniform, liftPx: Uniform, worldPerPx: Uniform, fill: Uniform, edge: Uniform): ShaderMaterial {
  return new ShaderMaterial({
    uniforms: {
      uSpin: spin,
      uLiftPx: liftPx,
      uWorldPerPx: worldPerPx,
      uFill: fill,
      uRim: edge,
      uRimPower: { value: NODE_RIM_POWER },
    },
    vertexShader: NODE_VERTEX,
    fragmentShader: NODE_FRAGMENT,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
  })
}

/** Lift of a node octahedron's centre above the surface (px): its hover plus its half height. */
function nodeLiftPx(px: CityArtPx): number {
  return px.nodeHover + px.nodeHalfHeight
}

interface EdgeBuild {
  readonly geometry: BufferGeometry
  readonly colours: BufferAttribute
  readonly spans: readonly EdgeSpan[]
  readonly progress: Float32Array
}

/**
 * One LineSegments geometry for every land road: its authoritative SurfacePath tessellated through
 * forEachPathSample, so every control point is emitted and each leg is subdivided. Vertices carry the route
 * progress (owner-colour blend); the radius lifts the line by the route's length. The vertex count
 * is budgeted by a counting pass that runs before the large vertex-buffer allocation (the small count array and the
 * sample closures are the only earlier allocations).
 */
function buildEdges(view: MatchSetupView): EdgeBuild {
  const edgeCount = view.edges.length
  // Budget first, shared with the route-ribbon cache: count every edge against one setup-wide cap, aborting as soon
  // as the running total is exceeded, before the large vertex-buffer allocation (only the small count array and the
  // sample closures exist before it).
  const counts = new Int32Array(edgeCount)
  let remaining = MAX_ROUTE_CENTRE_POINTS
  let vertexTotal = 0
  let segmentTotal = 0
  for (let e = 0; e < edgeCount; e++) {
    const edge = view.edges[e] as MapEdgeView
    const count = countRouteSamples(edge.path, remaining)
    if (count < 2) throw new InvariantViolation(`setup edge ${edge.a}-${edge.b} has fewer than two path samples`)
    counts[e] = count
    remaining -= count
    vertexTotal += count
    segmentTotal += count - 1
  }

  const positions = new Float32Array(vertexTotal * POSITION_COMPONENTS)
  const progress = new Float32Array(vertexTotal)
  const indices = new Uint32Array(segmentTotal * SEGMENT_VERTICES)
  const spans: EdgeSpan[] = []
  const point = new Vector3()
  let vertex = 0
  let cursor = 0
  for (let e = 0; e < edgeCount; e++) {
    const edge = view.edges[e] as MapEdgeView
    const firstVertex = vertex
    const vertexCount = counts[e] as number
    let index = 0
    let total = 0
    forEachPathSample(edge.path, ROUTE_STEP_RAD, (lat, lon, alongRad) => {
      latLonToVector3(lat, lon, GLOBE_ALTITUDE.network, point)
      point.toArray(positions, vertex * POSITION_COMPONENTS)
      // The route distance for now; normalized to progress once the route's length is known.
      progress[vertex] = alongRad
      if (index > 0) {
        indices[cursor++] = vertex - 1
        indices[cursor++] = vertex
      }
      total = alongRad
      index++
      vertex++
    })
    // Lift the route off the surface by its own length, apex at the halfway progress.
    for (let v = firstVertex; v < firstVertex + vertexCount; v++) {
      const s = total > 0 ? (progress[v] as number) / total : 0
      const radius = GLOBE_ALTITUDE.network + EDGE_LIFT_PER_RAD * total * Math.sin(Math.PI * s)
      const base = v * POSITION_COMPONENTS
      const x = positions[base] as number
      const y = positions[base + 1] as number
      const z = positions[base + 2] as number
      const length = Math.hypot(x, y, z)
      positions[base] = (x / length) * radius
      positions[base + 1] = (y / length) * radius
      positions[base + 2] = (z / length) * radius
      progress[v] = s
    }
    spans.push({ a: edge.a, b: edge.b, firstVertex, vertexCount })
  }

  const geometry = new BufferGeometry()
  const colours = new BufferAttribute(new Float32Array(vertexTotal * COLOUR_COMPONENTS), COLOUR_COMPONENTS)
  geometry.setAttribute('position', new BufferAttribute(positions, POSITION_COMPONENTS))
  geometry.setAttribute('aColor', colours)
  geometry.setIndex(new BufferAttribute(indices, 1))
  return { geometry, colours, spans, progress }
}
